import { FC, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, Cell, ResponsiveContainer, YAxis, XAxis } from 'recharts';

import { Box } from '@base-app';
import { COMMON_TRANSLATION } from '@constants/index';
import { colors } from '@modules/study-insights/constants';

import { CustomLabelProps, DemographicsBarChartProps } from './DemographicsBarChart.types';
import { StyledNoData } from './DemographicsBarChart.styles';

const DemographicsBarChart: FC<DemographicsBarChartProps> = ({ data }) => {
  const { t } = useTranslation([COMMON_TRANSLATION]);

  const xKey = 'name';
  const yKey = 'total';

  const renderCustomLabel = (props: CustomLabelProps): ReactElement => {
    const { x, y, value, width, height, index } = props;
    const fireOffset = value.toString().length < 7;
    const offset: number = fireOffset ? -8 : 5;
    return (
      <text fontSize={16} fill={colors.darkGray300} x={x + width - offset} y={y + height - 2}>
        {`${!isNaN(data[index].percentage) ? data[index].percentage.toFixed(1) : 0}%, ${value}`}
      </text>
    );
  };

  if (!data?.length) {
    return <StyledNoData>{t('no_data_available', { ns: COMMON_TRANSLATION })}</StyledNoData>;
  }

  return (
    <Box css={{ marginTop: '30px' }}>
      <ResponsiveContainer height={60 * data.length} debounce={50}>
        <BarChart data={data} layout="vertical" margin={{ right: 80 }}>
          <XAxis hide axisLine={false} type="number" />
          <YAxis
            axisLine={false}
            dataKey={xKey}
            fontSize={16}
            fontWeight={620}
            textAnchor="start"
            tick={{ fill: '#262E47', dx: -22, dy: -22 }}
            tickLine={false}
            type="category"
            yAxisId={0}
          />
          <Bar barSize={16} dataKey={yKey} label={renderCustomLabel} minPointSize={2} radius={[0, 3, 3, 0]}>
            {data.map((item: any) => {
              return <Cell x={30} key={item[xKey]} fill={'#62965A'} />;
            })}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default DemographicsBarChart;
